import styled from "@emotion/styled";
import React from "react";

const StyledParagraph = styled.div`
  label: StyledParagraph;
  margin: 0.5rem;
  line-height: 1.6;
`;

const StyledHeading = styled.h1`
  label: StyledHeading;
  margin: 2rem 0 1rem;
  font-weight: bold;
`;

const StyledList = styled.ul`
  label: StyledList;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const StyledAlignedDiv = styled.div<{ align?: string }>`
  label: StyledAlignedDiv;
  text-align: ${(props) => props.align || "left"};
`;

export const isEmpty = (value: string | null | undefined): boolean => {
  if (!value) return true;
  try {
    const isEmptyNode = (node: Record<string, unknown>): boolean => {
      if (node.text !== undefined) {
        return !node.text || (node.text as string).trim() === "";
      }
      return (node.children as Record<string, unknown>[])?.every(isEmptyNode) ?? true;
    };

    const parsed = JSON.parse(value?.replaceAll("\n", "") || "{}");
    if (Array.isArray(parsed)) {
      return parsed.every((block: Record<string, unknown>) => isEmptyNode(block));
    }
    return isEmptyNode(parsed);
  } catch (e) {
    console.error(e);
    return false;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderLeaf = (leaf: any, index: number, keyPrefix: string): React.ReactNode => {
  if (leaf.type) {
    return renderElement(leaf, index);
  }

  if (leaf.children) {
    return (
      leaf.children
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?.map((child: any, i: number) => renderLeaf(child, i, `${keyPrefix}-${i}`))
        .join("")
    );
  }

  let text: React.ReactNode = leaf.text ?? "";

  if (leaf.bold) {
    text = <strong key={`${keyPrefix}-bold`}>{text}</strong>;
  }
  if (leaf.italic) {
    text = <em key={`${keyPrefix}-italic`}>{text}</em>;
  }
  if (leaf.underline) {
    text = <u key={`${keyPrefix}-underline`}>{text}</u>;
  }
  if (leaf.code) {
    text = <code key={`${keyPrefix}-code`}>{text}</code>;
  }

  if (leaf.color) {
    text = (
      <span key={`${keyPrefix}-color`} style={{ color: leaf.color }}>
        {text}
      </span>
    );
  }

  return text;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderElement = (element: any, index: number): React.ReactNode => {
  const withAlignment = (content: React.ReactNode) => {
    return element.textAlign ? (
      <StyledAlignedDiv align={element.textAlign}>{content}</StyledAlignedDiv>
    ) : (
      content
    );
  };

  switch (element.type) {
    case "upload":
      return withAlignment(
        <img
          key={index}
          src={element.value?.url}
          alt={element.value?.filename}
          width={element.value?.width}
          height={element.value?.height}
        />,
      );

    case "paragraph":
      return withAlignment(
        <StyledParagraph key={index}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) => renderLeaf(child, i, `p-${index}-${i}`))}
        </StyledParagraph>,
      );

    case "heading-one":
      return withAlignment(
        <StyledHeading as="h1" key={index}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) =>
            renderLeaf(child, i, `h1-${index}-${i}`),
          )}
        </StyledHeading>,
      );

    case "heading-two":
      return withAlignment(
        <StyledHeading as="h2" key={index}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) =>
            renderLeaf(child, i, `h2-${index}-${i}`),
          )}
        </StyledHeading>,
      );

    case "bulleted-list":
    case "ul":
      return (
        <StyledList key={index}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) => renderElement(child, i))}
        </StyledList>
      );

    case "list-item":
    case "li":
      return (
        <li key={index}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) =>
            renderLeaf(child, i, `li-${index}-${i}`),
          )}
        </li>
      );

    case "link":
      return withAlignment(
        <a
          key={index}
          href={element.url}
          target={element.newTab ? "_blank" : undefined}
          rel={element.newTab ? "noopener noreferrer" : undefined}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) =>
            renderLeaf(child, i, `link-${index}-${i}`),
          )}
        </a>,
      );

    default:
      return withAlignment(
        <StyledParagraph key={index}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {element.children.map((child: any, i: number) =>
            renderLeaf(child, i, `default-${index}-${i}`),
          )}
        </StyledParagraph>,
      );
  }
};

export const RichText = ({ value }: { value?: string | null }) => {
  if (!value || value === "" || isEmpty(value)) return null;

  try {
    const parsedValue = JSON.parse(value?.replaceAll("\n", "") || "{}");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const content = parsedValue.map((element: any, i: number) => renderElement(element, i));
    return <>{content}</>;
  } catch (e) {
    console.error(e);
    return <>{value}</>;
  }
};

export const toPlainText = (value: string | null | undefined): string => {
  try {
    const extractText = (node: Record<string, unknown>): string => {
      if (node.text !== undefined) {
        return node.text as string;
      }
      return (node.children as Record<string, unknown>[])?.map(extractText).join("") ?? "";
    };

    const parsed = JSON.parse(value?.replaceAll("\n", "") || "{}");
    if (Array.isArray(parsed)) {
      return parsed.map(extractText).join("");
    }
    return extractText(parsed);
  } catch (e) {
    console.error(e);
    return "";
  }
};
